import { useCallback, useEffect, useState } from 'react';
import { useResource } from '../../hooks/useResource';
import { agentAccessApi, type AgentAccess } from '../../lib/api';
import type { Inbox } from '../../lib/types';
import { sx } from './styles';

/**
 * Agent → inbox access. An agent with NO inboxes selected sees every inbox
 * (tenant-wide, the default); selecting specific inboxes scopes them to just
 * those. Backed by the agent's Firebase `inboxIds` claim, so a change takes
 * effect on their next sign-in.
 */
export function AgentAccessPanel({ canEdit }: { canEdit: boolean }) {
  const { items: inboxes } = useResource<Inbox>('inboxes');
  const [agents, setAgents] = useState<AgentAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingUid, setSavingUid] = useState<string | null>(null);
  const [savedUid, setSavedUid] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await agentAccessApi.list();
    setAgents(r.items);
    setLoading(false);
  }, []);
  useEffect(() => { void load(); }, [load]);

  const inboxName = (id: string) => inboxes.find((ib) => ib.id === id)?.name ?? id;

  async function toggle(a: AgentAccess, inboxId: string) {
    if (!canEdit) return;
    const next = a.inboxIds.includes(inboxId)
      ? a.inboxIds.filter((x) => x !== inboxId)
      : [...a.inboxIds, inboxId];
    setSavingUid(a.uid);
    setAgents((prev) => prev.map((x) => (x.uid === a.uid ? { ...x, inboxIds: next } : x)));
    try {
      await agentAccessApi.set(a.uid, next);
      setSavedUid(a.uid);
      setTimeout(() => setSavedUid((u) => (u === a.uid ? null : u)), 1500);
    } catch (e) {
      alert(`Save failed: ${e instanceof Error ? e.message : e}`);
      await load(); // revert to server truth
    } finally {
      setSavingUid(null);
    }
  }

  return (
    <div>
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Agent access</h1>
      <p style={{ ...sx.hint, marginTop: 0, marginBottom: 16, fontSize: 13 }}>
        Choose which inboxes each agent can work. An agent with <b>no inbox selected sees all inboxes</b> (the default).
        Select inboxes to scope them. Changes apply the next time the agent signs in.
      </p>
      {loading ? (
        <p style={sx.hint}>Loading…</p>
      ) : agents.length === 0 ? (
        <p style={sx.hint}>No agents found for this tenant.</p>
      ) : (
        <table style={sx.table}>
          <thead>
            <tr>
              <th style={sx.th}>Agent</th>
              <th style={sx.th}>Role</th>
              <th style={sx.th}>Inbox access</th>
            </tr>
          </thead>
          <tbody>
            {agents.map((a) => (
              <tr key={a.uid}>
                <td style={sx.td}>
                  <div>{a.displayName}</div>
                  {a.email && <div style={{ color: '#9ca3af', fontSize: 12 }}>{a.email}</div>}
                </td>
                <td style={{ ...sx.td, fontSize: 12, color: '#6b7280' }}>
                  {a.role === 'tenant_admin' ? 'Admin' : 'Support'}
                </td>
                <td style={sx.td}>
                  {a.role === 'tenant_admin' ? (
                    <span style={{ ...sx.hint, fontSize: 12 }}>All inboxes (admin)</span>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                      {inboxes.map((ib) => {
                        const on = a.inboxIds.includes(ib.id);
                        return (
                          <button
                            key={ib.id}
                            type="button"
                            disabled={!canEdit || savingUid === a.uid}
                            onClick={() => toggle(a, ib.id)}
                            style={{
                              ...sx.btnSecondary, padding: '3px 9px',
                              background: on ? '#eff6ff' : 'white',
                              borderColor: on ? '#93c5fd' : '#d1d5db',
                              color: on ? '#1d4ed8' : '#374151', fontWeight: on ? 600 : 400,
                            }}
                          >
                            {ib.name}
                          </button>
                        );
                      })}
                      <span style={{ fontSize: 12, color: a.inboxIds.length === 0 ? '#059669' : '#9ca3af' }}>
                        {a.inboxIds.length === 0
                          ? 'all inboxes'
                          : a.inboxIds.map(inboxName).join(', ')}
                      </span>
                      {savedUid === a.uid && <span style={{ fontSize: 12, color: '#059669' }}>saved ✓</span>}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
